import React, { useState } from 'react'
import juice from 'juice'
import classNames from 'classnames'

import ContactField from './components/ContactField'
import TextField from './components/TextField'
import LayoutSelector from './components/layouts/LayoutSelector'
import Globals from './components/Globals'

import { ReactComponent as Icon1 } from './icons/layout1.svg'
import { ReactComponent as Icon2 } from './icons/layout2.svg'
import { ReactComponent as Icon3 } from './icons/layout3.svg'

import './app.scss'
import './signatures.scss'

if (process.env.NODE_ENV === 'development') {
    import('bulma/bulma.sass')
}

const App = () => {
    const [fields, setFields] = useState(Globals.textFieldDefaults)
    const [contactFields, setContactFields] = useState([])
    const [layout, setLayout] = useState(Globals.layout)

    const onTextFieldChanged = (
        key,
        value = null,
        color = null,
        font = null,
        url = null
    ) => {
        let newTextField = fields[key]
        if (value != null) {
            newTextField.value = value
        }

        if (color != null) {
            newTextField.color = color
        }

        if (font != null) {
            newTextField.font = font
        }

        if (url != null) {
            newTextField.url = url
        }

        setFields({ ...fields, [key]: newTextField })
    }

    const onContactFieldChanged = (
        index,
        value = null,
        label = null,
        color = null,
        font = null,
        url = null
    ) => {
        let newNumbers = contactFields.slice()
        if (value != null) {
            newNumbers[index].value = value
        }
        if (label != null) {
            newNumbers[index].label = label
        }
        if (color != null) {
            newNumbers[index].color = color
        }
        if (font != null) {
            newNumbers[index].font = font
        }
        if (url != null) {
            newNumbers[index].url = url
        }

        setContactFields(newNumbers)
    }

    const removeContactField = (index) => {
        let newNumbers = contactFields.slice()
        newNumbers.splice(index, 1)
        setContactFields(newNumbers)
    }

    const addContactField = () => {
        setContactFields([
            ...contactFields,
            {
                label: '',
                value: '',
                url: '',
                color: Globals.defaultColor,
                font: Globals.defaultFont,
            },
        ])
    }

    const onLayoutChanged = (e) => {
        if (!e.currentTarget.value || e.currentTarget.value.length === 0) {
            setLayout(1)
        } else {
            setLayout(parseInt(e.currentTarget.value))
        }
    }

    /**
     *
     * @returns a promise that returns the stylesheet that will style the signature
     */
    const getCss = async () => {
        let stylesheet = './signatures.css'

        if (process.env.NODE_ENV === 'production') {
            stylesheet =
                '/wp-content/themes/alta_moving/email-signatures/build/signatures.css'
        }

        return fetch(stylesheet)
            .then((response) => response.text())
            .then((styles) => {
                return styles
            })
    }

    /**
     * generate the html used for the signature
     */
    const juiceItUp = async () => {
        let html = document.querySelector('#output').innerHTML
        let css = await getCss()
        const options = {}

        let preview = document.querySelector('#preview')
        preview.srcdoc = juice.inlineContent(html, css, options)
    }

    /**
     * Copy contents of preview to
     * @returns null
     */
    const copy = () => {
        if (!navigator.clipboard) return
        try {
            let copyValue = document.querySelector('#preview').srcdoc
            navigator.clipboard.writeText(copyValue)
        } catch (error) {
            console.log('copy failed', error)
        }
    }

    return (
        <div className="app">
            <section className="section">
                <div className="container">
                    <h1 className="title is-1">Email Signature Generator</h1>
                    <div className="columns is-multiline is-mobile">
                        <div className="column is-6-desktop is-12-touch">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault()
                                }}
                            >
                                <div className="layouts my-6 is-hidden">
                                    <h2 className="title is-2">Layout</h2>
                                    <div className="field has-addons">
                                        <p className="control">
                                            <button
                                                value={1}
                                                className={classNames({
                                                    button: true,
                                                    'is-active': layout === 1,
                                                })}
                                                onClick={onLayoutChanged}
                                            >
                                                <span className="icon is-small">
                                                    <Icon1 alt="Layout 2 Icon" />
                                                </span>
                                                <span>Layout 1</span>
                                            </button>
                                        </p>
                                        <p className="control">
                                            <button
                                                value={2}
                                                className={classNames({
                                                    button: true,
                                                    'is-active': layout === 2,
                                                })}
                                                onClick={onLayoutChanged}
                                            >
                                                <span className="icon is-small">
                                                    <Icon2 alt="Layout 2 Icon" />
                                                </span>
                                                <span>Layout 2</span>
                                            </button>
                                        </p>
                                        <p className="control">
                                            <button
                                                value={3}
                                                className={classNames({
                                                    button: true,
                                                    'is-active': layout === 3,
                                                })}
                                                onClick={onLayoutChanged}
                                            >
                                                <span className="icon is-small">
                                                    <Icon3 alt="Layout 2 Icon" />
                                                </span>
                                                <span>Layout 3</span>
                                            </button>
                                        </p>
                                    </div>
                                </div>

                                <h2 className="title is-2">Contact Info</h2>
                                <TextField
                                    name={'name'}
                                    value={fields.name}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'jobTitle'}
                                    value={fields.jobTitle}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'email'}
                                    value={fields.email}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'pronouns'}
                                    value={fields.pronouns}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'company'}
                                    value={fields.company}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'website'}
                                    value={fields.website}
                                    onChange={onTextFieldChanged}
                                    url={true}
                                />
                                <TextField
                                    name={'address'}
                                    value={fields.address}
                                    onChange={onTextFieldChanged}
                                />
                                <TextField
                                    name={'bottom'}
                                    value={fields.bottom}
                                    onChange={onTextFieldChanged}
                                    area={true}
                                />

                                <div className="contactFields py-6">
                                    <h2 className="title is-2">
                                        Contact Details
                                    </h2>
                                    {contactFields.map((value, index) => {
                                        return (
                                            <ContactField
                                                key={index}
                                                remove={removeContactField}
                                                update={onContactFieldChanged}
                                                index={index}
                                                value={value}
                                            />
                                        )
                                    })}
                                    <button
                                        className="button is-success"
                                        onClick={addContactField}
                                    >
                                        +
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className="column is-6-desktop pl-6 is-12-touch">
                            <div className="is-flex is-align-items-center">
                                <h2 className="title is-2 pb-4">
                                    Live Preview
                                </h2>
                            </div>
                            <div id="output">
                                <LayoutSelector
                                    layout={layout}
                                    fields={fields}
                                    contactFields={contactFields}
                                />
                            </div>
                            <div className="my-5">
                                <button
                                    className="button is-warning"
                                    onClick={juiceItUp}
                                >
                                    GENERATE!
                                </button>
                            </div>

                            <h2 className="title is-2 pt-6">Output</h2>
                            <iframe
                                title="Email Signature Preview"
                                id="preview"
                            ></iframe>
                            <br />

                            <button
                                className="button is-primary"
                                onClick={copy}
                            >
                                COPY!
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default App
